import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  ColSpanParams,
  ExcelExportParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { isEqual, merge } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { SiteSelectDropdownComponent } from '@features/site-select-dropdown/site-select-dropdown.component';

import { SitesService } from '@models/sites/sites.service';

import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import {
  AgExpandHeaderComponent,
  AgExpandHeaderParams,
} from '@shared/components/ag-expand-header/ag-expand-header.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { InvestigatorSummary } from '@shared/services/gql.service';
import { TableService } from '@shared/services/table.service';
import { MainQuery } from '@shared/store/main/main.query';
import { AuxExcelStyles } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { InvestigatorSummaryColumns } from './grid-config/investigator-summary.columns';
import {
  InvestigatorSummaryRow,
  InvestigatorSummaryService,
} from './services/investigator-summary.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    FormsModule,
    ReactiveFormsModule,
    NoopValueAccessorDirective,
    SiteSelectDropdownComponent,
    MultiSelectDropdownComponent,
    ExportExcelButtonComponent,
    AgGridAngular,
    StickyGridDirective,
    AsyncPipe,
  ],
  providers: [InvestigatorSummaryService],
  selector: 'aux-investigator-summary',
  templateUrl: './investigator-summary.component.html',
})
export class InvestigatorSummaryComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);
  private investigatorSummaryService = inject(InvestigatorSummaryService);
  private mainQuery = inject(MainQuery);
  private router = inject(Router);
  private sitesService = inject(SitesService);

  excelOptions = {
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'amount_remaining':
        case 'invoiceables':
        case 'patient_visit_total':
        case 'total_cost_to_date':
          return 200;
        case 'forecast_cost_through_eot':
          return 230;
        case 'patient':
        case 'site_no':
          return 105;
        default:
          return 150;
      }
    },
    processCellCallback: (params) => {
      if (params.column.getColId() === 'investigator') {
        return params.value || Utils.zeroHyphen;
      }

      return params.value;
    },
    sheetName: 'Investigator Summary',
    shouldRowBeSkipped(params) {
      return !params.node?.data?.site_no;
    },
  } as ExcelExportParams;
  gridAPI!: GridApi<InvestigatorSummaryRow>;
  gridData$ = new BehaviorSubject<InvestigatorSummaryRow[]>([]);
  gridOptions$: BehaviorSubject<GridOptions> = new BehaviorSubject({
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellRenderer: AgCellWrapperComponent,
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    excelStyles: AuxExcelStyles,
    getRowClass: Utils.oddEvenRowClass,
    groupDisplayType: TableConstants.AG_SYSTEM.CUSTOM,
    suppressAggFuncInHeader: true,
    suppressMenuHide: true,
  } as GridOptions);
  gridRefresh$ = new BehaviorSubject(true);
  readonly ignoreColsForTotal = ['site_id', 'site_no', 'patient', 'investigator'];
  investigatorSummaryFiltersForm = this.formBuilder.group({
    patient_ids: null,
    site_ids: null,
  });
  patientOptions$ = this.investigatorSummaryService.patientOptions$;
  quickFilterText = '';
  sortChanged$ = new Subject();
  readonly siteNumberColumnSettings: ColDef = {
    cellClass: (x: CellClassParams) => {
      if (x?.data) {
        if (x?.data?.isTotal) {
          return 'text-left';
        }

        return [
          'grid-cell underline aux-link cursor-pointer justify-end text-left',
          TableConstants.STYLE_CLASSES.CELL_ALIGN_LEFT,
        ];
      }

      return ['text-left', TableConstants.STYLE_CLASSES.CELL_ALIGN_LEFT];
    },
    cellRenderer: TableConstants.AG_SYSTEM.AG_GROUP_CELL_RENDERER,
    cellRendererParams: {
      suppressCount: true,
    },
    colSpan: (params: ColSpanParams): number => {
      if (params.data?.isTotal) {
        return 2;
      }

      return 1;
    },
    field: 'site_no',
    filter: true,
    headerComponent: AgExpandHeaderComponent,
    headerComponentParams: {
      sortChanged$: this.sortChanged$,
    } as AgExpandHeaderParams,
    headerName: 'Site #',
    minWidth: 125,
    onCellClicked: (event: CellClickedEvent) => this.navigateToSite(event),
    resizable: true,
    showRowGroup: true,
    sort: 'asc',
    suppressSizeToFit: true,
    tooltipField: 'site_no',
  };

  ngOnInit(): void {
    this.mainQuery
      .select('trialKey')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.investigatorSummaryFiltersForm.reset()),
        switchMap(() => this.investigatorSummaryService.getDefaultFilters()),
        switchMap(() => this.sitesService.get())
      )
      .subscribe(() => {
        this.initTable();
      });

    this.filtersValueChanges();
  }

  private filterGrid(filters: { patient_ids: string[]; site_ids: string[] }): void {
    const siteNumbers: string[] = [];
    const siteNoFilterInstance = this.gridAPI.getFilterInstance('site_no');
    const patientIdsFilterInstance = this.gridAPI.getFilterInstance('patient');

    filters.site_ids?.forEach((siteId: string) => {
      const siteNo = this.investigatorSummaryService.siteMap.get(siteId)?.site_no;

      if (siteNo) {
        siteNumbers.push(siteNo);
      }
    });

    siteNoFilterInstance?.setModel({
      filterType: 'text',
      type: 'Equals',
      values: siteNumbers?.length ? siteNumbers : null,
    });

    patientIdsFilterInstance?.setModel({
      filterType: 'text',
      type: 'Equals',
      values: filters.patient_ids?.length ? filters.patient_ids : null,
    });

    this.gridAPI.onFilterChanged();
    this.setTotalRowData();
  }

  private filtersValueChanges(): void {
    this.investigatorSummaryFiltersForm.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        distinctUntilChanged(isEqual),
        tap((filters) => this.filterGrid(filters))
      )
      .subscribe();
  }

  getDynamicExcelParams = (): ExcelExportParams => {
    const name = this.mainQuery.getSelectedTrial()?.short_name;
    const totals = this.gridAPI.getPinnedBottomRow(0)?.data;
    const columns = Object.entries(totals)
      .map(([key, value]) => (typeof value === 'number' ? key : null))
      .filter((key) => key) as string[];

    const columnKeys = this.gridAPI
      .getAllDisplayedColumns()
      .map((x) => {
        return x.getId().includes('site_no') ? 'site_no' : x.getId();
      })
      .filter((x) => x !== 'spacerColumn');

    const appendContent: ExcelExportParams['appendContent'] = totals
      ? [
          {
            cells: [
              {
                data: { type: 'String', value: `Total` },
                styleId: 'total_row_header',
              },
              ...TableService.getTotalRowForExcel({
                aggregateColumns: columns,
                excludeColumns: ['site_no_1', 'patient', 'site_id', 'site_no'],
                gridApi: this.gridAPI,
                totalData: totals,
              }),
            ],
          },
        ]
      : [{ cells: [] }];

    return {
      columnKeys,
      fileName: `auxilius-patient-summary_${dayjs().format('YYYY.MM.DD-HHmmss')}.xlsx`,
      ...this.excelOptions,
      appendContent,
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${name}` },
              mergeAcross: 1,
              styleId: 'first_row',
            },
          ],
        },
      ],
      shouldRowBeSkipped(params) {
        return params.node?.data?.site_no === 'Total' || !params.node?.data;
      },
    };
  };

  gridSizeChanged() {
    this.gridAPI?.sizeColumnsToFit();
  }

  private initTable(): void {
    this.toggleLoaders(true);
    this.investigatorSummaryService
      .fetchAll()
      .subscribe((response: GraphqlResponse<Array<InvestigatorSummary>>) => {
        const dynamicColumns: ColDef[] =
          this.investigatorSummaryService.getGridDynamicColumns(response);

        this.gridAPI?.setGridOption('columnDefs', [
          ...InvestigatorSummaryColumns(this.siteNumberColumnSettings, dynamicColumns),
        ]);
        this.gridData$.next(this.investigatorSummaryService.getGridData(response, dynamicColumns));
        this.setTotalRowData(this.gridData$.getValue());

        this.gridAPI?.applyColumnState({
          state: [{ colId: 'site_no', sort: 'asc' }],
        });

        this.gridAPI?.sizeColumnsToFit();
        this.toggleLoaders(false);
      });
  }

  private navigateToSite(event: CellClickedEvent): void {
    const { site_id } = event.data;

    if (!site_id) return;

    void this.router.navigate([
      `${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/${site_id}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.DETAILS}`,
    ]);
  }

  onGridReady({ api }: GridReadyEvent): void {
    this.gridAPI = api;
  }

  setTotalRowData(initialRows?: InvestigatorSummaryRow[]): void {
    const rowData: InvestigatorSummaryRow[] = [];

    this.gridAPI?.forEachNodeAfterFilter((node) => {
      if (node.data) {
        rowData.push(node.data);
      }
    });

    this.gridAPI?.setGridOption('pinnedBottomRowData', [
      merge(
        {
          isTotal: true,
          site_no: 'Total',
        },
        TableService.generateTotalRow(
          (initialRows || rowData) as Record<string, number>[],
          this.ignoreColsForTotal
        )
      ),
    ]);
  }

  private toggleLoaders(isLoading: boolean): void {
    this.gridRefresh$.next(isLoading);
  }
}
